/**
 * STREET ADDRESS DERIVATIONS
 * The pure core shared by every writer of a `StreetAddress`: the display-string formatter, the
 * country classifier that turns any English country name or legacy spelling in the corpus into
 * ISO-3166-1 alpha-2, and the fold that moves a shipping contact into the address it describes.
 * Consumers are `react` (AddressUI, Excel export), `signifyApiAction`
 * (new orders) and the SYD-2308 migration CLIs under `utils/`.
 *
 * Invariant: nothing here touches a Firestore SDK. The consumers pin different `firebase-admin`
 * majors, so the paged scan and the batched write loop stay per package while these derivations —
 * the parts that must be byte-identical — are shared. Same reason `System.ReferencedListPlan.types`
 * exists.
 *
 * Invariant: `foldShippingContactIntoAddress` is the ONLY fold. The migration folds history with it
 * and `signifyApiAction` folds new orders with it; if the two ever diverge, new orders write a shape
 * the migration did not produce and its second dry run never reports zero.
 *
 * Invariant: `classifyCountry` never infers a country from a subdivision code. `CA` as a state is
 * Californian in the US and meaningless in Ontario, and a migration may not guess.
 */

import type { StreetAddress } from './System.FieldTypes.types';
import { ISO_3166_COUNTRY_NAMES } from './System.IsoCountries.types';

export type AddressComponents = NonNullable<StreetAddress['components']>;

/**
 * The keys that place a shipment somewhere. An address carrying none of these has a recipient but
 * no destination — legal, and the shape 48 of the SYD-2308 migration's live rows land in.
 */
export const ADDRESS_LOCATION_KEYS = [
  'street',
  'street2',
  'city',
  'state',
  'postalCode',
  'country',
] as const;

/**
 * Every key an address may carry. Disjoint from `TelephoneNumber`'s (`countryCode`, `number`,
 * `extension`), which is what lets a components map be classified as one or the other — the two
 * share the `{ formatted, components }` envelope and are otherwise indistinguishable.
 */
export const ADDRESS_COMPONENT_KEYS = [
  ...ADDRESS_LOCATION_KEYS,
  'recipientName',
  'company',
  'phone',
  'email',
] as const;

/** True when `components` is an address rather than another `{ formatted, components }` envelope. */
export const isAddressComponents = (value: unknown): value is AddressComponents => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return ADDRESS_COMPONENT_KEYS.some((key) => key in (value as Record<string, unknown>));
};

const hasFilledKey = (
  components: AddressComponents | undefined,
  keys: readonly (keyof AddressComponents)[],
): boolean =>
  !!components && keys.some((key) => {
    const value = components[key];
    return typeof value === 'string' && value.trim() !== '';
  });

/** True when the address places a shipment somewhere, as opposed to only naming a recipient. */
export const hasShippingLocation = (components: AddressComponents | undefined): boolean =>
  hasFilledKey(components, ADDRESS_LOCATION_KEYS);

/**
 * The keys a postal payload cannot be built without. `state` and `country` alone locate nothing a
 * carrier can deliver to, and `street2` qualifies a street that is not there.
 */
export const POSTAL_DESTINATION_KEYS = ['street', 'city', 'postalCode'] as const;

/**
 * True when the address can be posted, not merely placed. This is the predicate for an API boundary
 * that must fill required address fields: Signify's `OrderAddress` declares `street`, `town` and
 * `postalCode` as non-optional strings, so a `{ state: 'IL' }`-only address must send no address at
 * all rather than three empty required fields. `hasShippingLocation` answers the broader question
 * the SYD-2308 migration asks — "does this address place a shipment anywhere" — and is true for
 * exactly that state-only shape.
 */
export const hasPostalDestination = (components: AddressComponents | undefined): boolean =>
  hasFilledKey(components, POSTAL_DESTINATION_KEYS);

/** Every ISO-3166-1 alpha-2 code. The membership test a two-letter country string must pass. */
export const ISO_3166_ALPHA2: ReadonlySet<string> = new Set(Object.keys(ISO_3166_COUNTRY_NAMES));

const foldDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

/**
 * English country name → alpha-2, keyed by `trim().toLowerCase()` with diacritics folded away, so
 * `Türkiye`, `Turkiye`, `México` and `Mexico` all resolve. This is what classifies the Google Places
 * LONG country names (`Germany`, `United Kingdom`) that `parseAddressComponents` stored for every
 * Places-selected address, and the labels the retired country `Select` wrote verbatim.
 */
export const ISO_3166_NAME_TO_ALPHA2: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(ISO_3166_COUNTRY_NAMES).map(([code, name]) => [
    foldDiacritics(name.toLowerCase()),
    code,
  ]),
);

/**
 * Country spellings the ISO name table cannot produce: the legacy spellings observed in the corpus
 * and the common variants of the fourteen labels the retired dropdown offered — abbreviations,
 * alpha-3 codes, endonyms and constituent nations. Keyed by `trim().toLowerCase()`. A name the table
 * already carries belongs there, not here.
 */
export const LEGACY_COUNTRY_ALIASES: Readonly<Record<string, string>> = {
  usa: 'US',
  'u.s': 'US',
  'u.s.': 'US',
  'u.s.a': 'US',
  'u.s.a.': 'US',
  'united states of america': 'US',
  'the united states': 'US',
  america: 'US',
  can: 'CA',
  uk: 'GB',
  'u.k': 'GB',
  'u.k.': 'GB',
  gbr: 'GB',
  britain: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  aus: 'AU',
  deu: 'DE',
  ger: 'DE',
  deutschland: 'DE',
  fra: 'FR',
  jpn: 'JP',
  nippon: 'JP',
  chn: 'CN',
  prc: 'CN',
  "people's republic of china": 'CN',
  ind: 'IN',
  bra: 'BR',
  brasil: 'BR',
  mex: 'MX',
  'méxico': 'MX',
  esp: 'ES',
  'españa': 'ES',
  espana: 'ES',
  ita: 'IT',
  italia: 'IT',
  nld: 'NL',
  holland: 'NL',
  'the netherlands': 'NL',
};

/**
 * Values that occupy the country slot while carrying no country information.
 *
 * Deliberately case-sensitive in effect: `classifyCountry` tests ISO membership on the exact
 * uppercase value BEFORE this set, so `NA` is Namibia while `na`, `Na`, `n/a` and `N/A` are "not
 * applicable" and get deleted. An ISO code is written uppercase; an abbreviation for nothing is not.
 * Removing `na` here, or moving the noise test ahead of the ISO test, deletes a valid country.
 */
export const COUNTRY_NOISE: ReadonlySet<string> = new Set(['.', '-', '--', 'n/a', 'na', 'none']);

/**
 * `UNI` and `Uni` are three-character truncations, not case variants: they are ambiguous across
 * United States, United Kingdom and United Arab Emirates. A human resolves them by reading the
 * sibling components; this module refuses to.
 */
export const AMBIGUOUS_COUNTRY_PREFIXES: Readonly<Record<string, readonly string[]>> = {
  uni: ['US', 'GB', 'AE'],
};

/**
 * US subdivision codes, for the one inference that is safe to make at the API boundary: a blank
 * country beside a US state means a domestic order. Never applied to stored data — blank stays
 * blank in Firestore, because "not captured" and "captured as US" are different facts.
 */
export const US_STATE_CODES: ReadonlySet<string> = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);

export type CountryVerdict =
  /** `null`, `undefined` or blank. Means "not captured" — never backfilled. */
  | { kind: 'absent' }
  | { kind: 'iso'; value: string }
  | { kind: 'normalized'; from: string; to: string }
  | { kind: 'noise'; from: string }
  | { kind: 'ambiguous'; from: string; candidates: readonly string[] }
  | { kind: 'unrecognized'; from: string };

/**
 * Classify whatever sits in the country slot. Resolution order, and it is load-bearing:
 * 1. `null`, `undefined` or blank → `absent`.
 * 2. an exactly-uppercase two-letter code that `ISO_3166_ALPHA2` contains → `iso`. Ahead of noise so
 *    `NA` stays Namibia, and a membership test rather than a shape test so `UK` and `XX` do not pass
 *    themselves off as ISO — `UK` is not ISO-3166-1, which spells that country `GB`.
 * 3. `COUNTRY_NOISE` → `noise`.
 * 4. `AMBIGUOUS_COUNTRY_PREFIXES` → `ambiguous`.
 * 5. `LEGACY_COUNTRY_ALIASES` → `normalized`.
 * 6. `ISO_3166_NAME_TO_ALPHA2` → `normalized`. Covers every English country name.
 * 7. a two-letter code in the wrong case (`us`, `Us`) → `normalized` to the uppercase form.
 * 8. anything else → `unrecognized`.
 */
export const classifyCountry = (raw: unknown): CountryVerdict => {
  if (raw === null || raw === undefined) return { kind: 'absent' };
  const from = String(raw);
  const trimmed = from.trim();
  if (trimmed === '') return { kind: 'absent' };
  if (/^[A-Z]{2}$/.test(trimmed) && ISO_3166_ALPHA2.has(trimmed)) {
    return { kind: 'iso', value: trimmed };
  }
  const key = trimmed.toLowerCase();
  if (COUNTRY_NOISE.has(key)) return { kind: 'noise', from };
  const ambiguous = AMBIGUOUS_COUNTRY_PREFIXES[key];
  if (ambiguous) return { kind: 'ambiguous', from, candidates: ambiguous };
  const alias = LEGACY_COUNTRY_ALIASES[key];
  if (alias) return { kind: 'normalized', from, to: alias };
  const named = ISO_3166_NAME_TO_ALPHA2[key] ?? ISO_3166_NAME_TO_ALPHA2[foldDiacritics(key)];
  if (named) return { kind: 'normalized', from, to: named };
  const upper = trimmed.toUpperCase();
  if (ISO_3166_ALPHA2.has(upper)) return { kind: 'normalized', from, to: upper };
  return { kind: 'unrecognized', from };
};

/**
 * The ISO alpha-2 code to send an API for a stored country, or `undefined` when the value carries
 * no country. Tolerates the legacy spellings still in Firestore, so the API boundary is correct
 * both before the SYD-2308 sanitation runs and after it — the mapper never has to be re-timed
 * against the migration. Ambiguous and unrecognized values resolve to `undefined` rather than a
 * guess: sending nothing is recoverable, sending the wrong country is not.
 */
export const toIsoCountry = (raw: unknown): string | undefined => {
  const verdict = classifyCountry(raw);
  if (verdict.kind === 'iso') return verdict.value;
  if (verdict.kind === 'normalized') return verdict.to;
  return undefined;
};

/**
 * The display string, ordered as a postal label: recipient lines, then street, then locality.
 * An address carrying no recipient formats exactly as it did before SYD-2308 — the geographic
 * parts, their order and their separator are unchanged, which is what lets the migration leave
 * derived `formatted` strings alone on rows it does not otherwise touch.
 */
export const formatStreetAddressFromComponents = (components: AddressComponents): string => {
  const parts = [
    components.recipientName,
    components.company,
    components.street,
    components.street2,
    [components.city, components.state].filter(Boolean).join(', '),
    components.postalCode,
    components.country,
  ].filter(Boolean);
  return parts.join(', ');
};

/**
 * True when `formatted` is this formatter's own output over the current components — i.e. a cache
 * that is safe to refresh. False means the string is provenance: a Google `formatted_address` or
 * something a human typed, which a migration has no mandate to destroy.
 */
export const isDerivedFormatted = (address: StreetAddress): boolean =>
  (address.formatted ?? '') === formatStreetAddressFromComponents(address.components ?? {});

/**
 * Normalizes whatever is stored in an address cell into a `StreetAddress`, or `undefined` when the
 * cell is empty. Strings are real: the extraction pipeline has no `address` coercion branch, so an
 * imported address column lands as raw text. They normalize the way `useAddressUI` and clipboard
 * paste already do — the whole line becomes `street` — so no writer loses the value.
 */
export const asStreetAddress = (value: unknown): StreetAddress | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : { formatted: value, components: { street: value } };
  }
  if (typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as StreetAddress;
};

/** A shipping contact carried beside an address, as the retired composite sub-fields held it. */
export type ShippingContact = {
  recipientName?: string;
  company?: string;
  phone?: string;
};

export type FoldConflict = {
  key: keyof ShippingContact;
  incoming: string;
  existing: string;
};

const isFilled = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const FOLD_KEYS = ['recipientName', 'company', 'phone'] as const;

/**
 * The fold keys `formatStreetAddressFromComponents` renders, in label order. A fold that touches
 * only `phone` changes no display string, which is why a phone-only fold leaves `formatted` alone.
 */
const FOLD_DISPLAY_KEYS: readonly (typeof FOLD_KEYS)[number][] = ['recipientName', 'company'];

type FoldResolution =
  | { kind: 'skip' }
  | { kind: 'fill'; key: (typeof FOLD_KEYS)[number]; value: string }
  | { kind: 'conflict'; conflict: FoldConflict };

/**
 * Move a shipping contact into the address it describes.
 *
 * `created` reports that there was no address to fold into — the case for 48 of the 90 live rows,
 * which carry a contact name with no address at all. Read it with `changed`: `created && !changed`
 * means nothing existed and nothing was supplied, so the caller has no address to write, while
 * `created && changed` means one was synthesized. A synthesized value carries only the keys actually
 * supplied plus `formatted`: no `alternativeData` key (an empty object would read as Places
 * provenance that is not there) and no `street2: null` (the 20,290 explicit nulls in the corpus are
 * an old importer's artifact, not a convention).
 *
 * A key already holding a different non-empty value is reported as a conflict and left alone; the
 * caller decides whether that fails the run.
 *
 * `formatted` always ends up mentioning a folded name, because the collapsed table cell, the Excel
 * export and the Algolia record all render it and nothing else. When the stored string is this
 * formatter's own output — or blank, or synthesized — it is re-derived whole. When it is provenance
 * a migration may not destroy (a Google `formatted_address`, or something a human typed) the name
 * lines this fold actually added are PREPENDED to it instead, so the geography tail stays byte-
 * identical. Idempotent either way: a second fold finds the key filled, reports `changed: false`
 * and rewrites nothing.
 */
export const foldShippingContactIntoAddress = (
  address: unknown,
  contact: ShippingContact,
): { value: StreetAddress; created: boolean; changed: boolean; conflicts: FoldConflict[] } => {
  const normalized = asStreetAddress(address);
  const created = normalized === undefined;
  const base: StreetAddress = normalized ?? {};
  const existingComponents: AddressComponents = base.components ?? {};

  const resolutions: FoldResolution[] = FOLD_KEYS.map((key) => {
    const incoming = contact[key];
    if (!isFilled(incoming)) return { kind: 'skip' };
    const existing = existingComponents[key];
    if (isFilled(existing)) {
      return existing.trim() === incoming.trim()
        ? { kind: 'skip' }
        : { kind: 'conflict', conflict: { key, incoming, existing } };
    }
    return { kind: 'fill', key, value: incoming.trim() };
  });

  const filled = resolutions.flatMap((resolution) =>
    resolution.kind === 'fill' ? [resolution] : [],
  );
  const conflicts = resolutions.flatMap((resolution) =>
    resolution.kind === 'conflict' ? [resolution.conflict] : [],
  );

  if (filled.length === 0) return { value: base, created, changed: false, conflicts };

  const components = filled.reduce<AddressComponents>(
    (accumulated, { key, value }) => ({ ...accumulated, [key]: value }),
    { ...existingComponents },
  );

  const stored = base.formatted ?? '';
  const prepended = filled
    .filter(({ key }) => FOLD_DISPLAY_KEYS.includes(key))
    .map(({ value }) => value);
  const formatted =
    created || stored.trim() === '' || isDerivedFormatted(base)
      ? formatStreetAddressFromComponents(components)
      : [...prepended, stored].join(', ');

  return {
    value: { ...base, components, formatted },
    created,
    changed: true,
    conflicts,
  };
};
